import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttTable, SttNoResults } from '../../../sporttia/all';
import { getPath } from '../../Pages';
import { formatPriceByLocale } from '../../../lib/utils';
import useScsService from '../../../services/ScsService';
import type { Group } from '../../../types/models/Group';
import SttError from '../../../components/error/SttError';

const flatGroupsAndChildren = (scGroups: Group[]) =>
    scGroups.reduce((groups: Group[], group) => {
        const { children, ...withoutChildren } = group;
        children?.rows.forEach((child: any) => {
            groups.push(child);
        });
        groups.push(withoutChildren);
        return groups;
    }, []);

type Params = {
    id: string;
};

export default function AbonoList() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { id: idSc } = useParams<Params>();

    const scsService = useScsService();

    const getScsGroupsQuery = scsService.useGetScsGroups<Group[]>(
        Number(idSc),
        {
            types: 'ABONADOS',
            purchaseOnline: true,
            rows: 100,
            includeChildren: true,
            inTime: true,
            inFuture: true,
            onlyRoots: false,
        },
        {
            select: (data: any) => flatGroupsAndChildren(data.rows),
        } as any,
    );

    if (getScsGroupsQuery.isLoading) {
        return (
            <Container maxWidth="md">
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (getScsGroupsQuery.isError) {
        return <SttError />;
    }

    if (!getScsGroupsQuery.data) {
        return null;
    }

    return (
        <Container maxWidth="md">
            {!getScsGroupsQuery.data.length ? (
                <SttNoResults />
            ) : (
                <SttTable
                    data={{
                        count: getScsGroupsQuery.data.length,
                        rows: getScsGroupsQuery.data,
                    }}
                    columns={[
                        {
                            title: t('labels.name'),
                            type: 'link',
                            align: 'left',
                            value: (row: Group) => row.name,
                            onClick: (row: Group) => {
                                navigate(
                                    getPath('buyAbono', {
                                        state: {
                                            idSc,
                                            idGroup: row.id,
                                        },
                                    }),
                                );
                            },
                        },
                        {
                            title: t('activity.enrollment'),
                            type: 'text',
                            value: (row: Group) =>
                                formatPriceByLocale(
                                    row?.enrollmentPrice,
                                    row?.enrollmentShape?.currency ?? undefined,
                                ),
                        },
                        {
                            title: t('activity.fee'),
                            type: 'text',
                            value: (row: Group) =>
                                row.feeInterval &&
                                `${formatPriceByLocale(
                                    row?.feePrice,
                                    row?.feeShape?.currency ?? undefined,
                                )}/${t(row.feeInterval)}`,
                        },
                    ]}
                />
            )}
        </Container>
    );
}
